import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import useUser from '@hooks/api/queries/users/useUser';
import { Box, CircularProgress, Dialog, DialogTitle } from '@mui/material';
import QueryKeys from '@src/constants/queryKeys';
import useUserUpdate, {
  EditUserFormValues,
} from '@src/hooks/api/mutations/useUserUpdate';
import { Actions } from '@src/types/Actions';
import storeConnector from '@store/storeConnector';
import { useQueryClient } from '@tanstack/react-query';
import EditUserForm from '../Forms/EditUserForm';

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  actions: Actions;
  userId: string;
}

function EditUserDialog({
  open,
  onClose,
  actions,
  userId,
}: EditUserDialogProps) {
  const alias = useAlias();
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useUser(userId);
  const { mutate: updateUser, isPending } = useUserUpdate(userId);

  const handleSubmit = async (values: EditUserFormValues) => {
    updateUser(values, {
      onSuccess: () => {
        actions.notify(alias('Successfully updated user.'));
        queryClient.invalidateQueries({ queryKey: [QueryKeys.user, userId] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.users] });
        onClose();
      },
      onError: (error: Error) => {
        const customError = error.cause as { error?: { message: string } };
        actions.notifyError(
          customError?.error?.message ?? alias('Failed to update user.'),
        );
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <FormattedAlias id='Edit User' />
      </DialogTitle>
      {isLoading || user === undefined ? (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <EditUserForm
          onCancel={onClose}
          onSubmit={handleSubmit}
          isLoading={isPending}
          user={user}
        />
      )}
    </Dialog>
  );
}

export default storeConnector(EditUserDialog, {});
